import { useEffect, useRef, useState } from "react";
import Supercluster from "supercluster";
import { WebSocketManager, ConnectionState, type MessageHandlers } from "../../../utils/WebSocketManager";
import { usePlatformAPI } from "../../../api/PlatformProvider";

// --- Types ---

export type Incident = {
    id: string;
    severity: string;
    position: { coordinates: [number, number] };
};

export type IncidentDetail = {
    id: string;
    categoryName: string;
    typeName: string;
    severity: string;
    summary: string;
    description: string;
    countryISO: string;
    town?: string;
    feedID: string;
    tags: string[];
    startedAt: string;
    endedAt?: string;
    ongoing: boolean;
    reportedAt?: string;
    updatedAt?: string;
    source?: string;
    casualties?: { dead?: number; injured?: number; missing?: number };
    position?: { type: string; coordinates: [number, number] };
    reference?: Array<{ medium: string; link: string; title: string; publishedAt: string }>;
};

export type IncidentPreview = {
    id: string;
    summary: string;
    categoryName?: string;
    typeName?: string;
    severity?: string;
    displaySeverity: string;
    startedAt?: string;
    town?: string;
    countryISO?: string;
    tags?: string[];
};

type RenderedPoint = {
    id: string;
    x: number;
    y: number;
    incidents: Incident[];
    isCluster: boolean;
    clusterID?: number;
};

// --- Shaders ---

const ICON_VERT = `
    attribute vec2 a_position;
    attribute float a_highlighted;
    void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
        gl_PointSize = a_highlighted > 0.5 ? 32.0 : 24.0;
    }
`;

const ICON_FRAG = `
    #extension GL_EXT_shader_texture_lod : enable
    precision mediump float;
    uniform sampler2D u_texture;
    void main() {
        vec2  uv   = gl_PointCoord - 0.5;
        float dist = length(uv);

        float sm        = 0.04;
        float fillR     = 0.5;
        float iconScale = 0.71;

        if (dist > fillR) discard;

        // Red filled circle, antialiased edge
        float fillAlpha = smoothstep(fillR, fillR - sm, dist);
        vec4 col = vec4(0.863, 0.149, 0.149, fillAlpha);  // #dc2626

        // Icon sampled from scaled-down UV region
        vec2 iconUV = (gl_PointCoord - 0.5) / iconScale + 0.5;
        if (iconUV.x >= 0.0 && iconUV.x <= 1.0 && iconUV.y >= 0.0 && iconUV.y <= 1.0) {
            #ifdef GL_EXT_shader_texture_lod
                vec4 iconColor = texture2DLodEXT(u_texture, iconUV, 0.0);
            #else
                vec4 iconColor = texture2D(u_texture, iconUV, -1.0);
            #endif
            // Texture is premultiplied
            col.rgb = iconColor.rgb + col.rgb * (1.0 - iconColor.a);
        }

        if (col.a < 0.01) discard;
        // Premultiply for correct edge blending
        gl_FragColor = vec4(col.rgb * col.a, col.a);
    }
`;

// --- WebGL helpers ---

function compileShader(gl: WebGLRenderingContext, type: number, src: string): WebGLShader {
    const shader = gl.createShader(type)!;
    gl.shaderSource(shader, src);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        throw new Error(gl.getShaderInfoLog(shader) ?? "Shader compile error");
    }
    return shader;
}

function buildProgram(gl: WebGLRenderingContext, vert: string, frag: string): WebGLProgram {
    const vs = compileShader(gl, gl.VERTEX_SHADER, vert);
    const fs = compileShader(gl, gl.FRAGMENT_SHADER, frag);
    const prog = gl.createProgram()!;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    return prog;
}

function loadTexture(gl: WebGLRenderingContext, src: string, onLoad?: () => void): WebGLTexture {
    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 0]));
    const img = new Image();
    img.onload = () => {
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 1);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 0);
        gl.generateMipmap(gl.TEXTURE_2D);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        const ext = gl.getExtension("EXT_texture_filter_anisotropic")
                 ?? gl.getExtension("MOZ_EXT_texture_filter_anisotropic")
                 ?? gl.getExtension("WEBKIT_EXT_texture_filter_anisotropic");
        if (ext) gl.texParameterf(gl.TEXTURE_2D, ext.TEXTURE_MAX_ANISOTROPY_EXT, 4);
        onLoad?.();
    };
    img.src = src;
    return tex;
}

// --- Overlay factory ---

const CLUSTER_BG   = "#e30000";
const CLUSTER_TEXT = "#ffffff";
const CLICK_RADIUS = 30;
const CLUSTER_RADIUS = 50;
const CLUSTER_MAX_ZOOM = 14;

type DrawPoint = {
    lng: number;
    lat: number;
    incident?: Incident;
    isCluster: boolean;
    clusterID?: number;
    count?: number;
};

function createIncidentsOverlay(
    map: google.maps.Map,
    onRenderedPoints: (pts: RenderedPoint[]) => void,
) {
    const glCanvas = document.createElement("canvas");
    glCanvas.style.position = "absolute";
    glCanvas.style.top = "0";
    glCanvas.style.left = "0";
    glCanvas.style.pointerEvents = "none";
    map.getDiv().appendChild(glCanvas);

    const clusterCanvas = document.createElement("canvas");
    clusterCanvas.style.position = "absolute";
    clusterCanvas.style.top = "0";
    clusterCanvas.style.left = "0";
    clusterCanvas.style.pointerEvents = "none";
    map.getDiv().appendChild(clusterCanvas);

    const gl = glCanvas.getContext("webgl", { alpha: true, premultipliedAlpha: true })!;
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    const iconProg = buildProgram(gl, ICON_VERT, ICON_FRAG);
    const iconTex  = loadTexture(gl, "/assets/mapicons/incident_transparent.png");

    let isReady = false;
    const overlay = new (class extends google.maps.OverlayView {
        onAdd()    { isReady = true; }
        draw()     {}
        onRemove() { isReady = false; }
        getProjectionSafe(): google.maps.MapCanvasProjection | null {
            if (!isReady) return null;
            try { return this.getProjection(); } catch { return null; }
        }
    })();
    overlay.setMap(map);

    let drawPoints: DrawPoint[] = [];
    let hoveredIDs = new Set<string>();
    let rafId: number | null = null;

    function resizeCanvases(width: number, height: number, dpr: number) {
        for (const c of [glCanvas, clusterCanvas]) {
            c.style.width  = `${width}px`;
            c.style.height = `${height}px`;
            c.width  = width  * dpr;
            c.height = height * dpr;
        }
    }

    function draw() {
        const projection = overlay.getProjectionSafe();
        if (!projection) return;

        const mapDiv = map.getDiv() as HTMLElement;
        const dpr    = window.devicePixelRatio || 1;
        const width  = mapDiv.offsetWidth;
        const height = mapDiv.offsetHeight;
        if (!width || !height) return;

        resizeCanvases(width, height, dpr);

        gl.viewport(0, 0, glCanvas.width, glCanvas.height);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);

        const ctx2d = clusterCanvas.getContext("2d")!;
        ctx2d.clearRect(0, 0, clusterCanvas.width, clusterCanvas.height);
        ctx2d.scale(dpr, dpr);

        const renderedPoints: RenderedPoint[] = [];

        if (drawPoints.length === 0) {
            ctx2d.setTransform(1, 0, 0, 1, 0, 0);
            onRenderedPoints(renderedPoints);
            return;
        }

        const individuals = drawPoints.filter((dp) => !dp.isCluster);
        const clusters    = drawPoints.filter((dp) =>  dp.isCluster);

        // --- Draw individual incidents via WebGL ---
        const positions:   number[] = [];
        const highlighted: number[] = [];

        for (const dp of individuals) {
            if (!isFinite(dp.lng) || !isFinite(dp.lat)) continue;
            const pixel = projection.fromLatLngToContainerPixel(new google.maps.LatLng(dp.lat, dp.lng));
            if (!pixel) continue;

            positions.push((pixel.x / width) * 2 - 1, 1 - (pixel.y / height) * 2);
            highlighted.push(dp.incident && hoveredIDs.has(dp.incident.id) ? 1.0 : 0.0);
            renderedPoints.push({ id: dp.incident?.id ?? "", x: pixel.x, y: pixel.y, incidents: dp.incident ? [dp.incident] : [], isCluster: false });
        }

        if (positions.length > 0) {
            const count = positions.length / 2;

            const posBuf = gl.createBuffer()!;
            gl.bindBuffer(gl.ARRAY_BUFFER, posBuf);
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.DYNAMIC_DRAW);

            const hlBuf = gl.createBuffer()!;
            gl.bindBuffer(gl.ARRAY_BUFFER, hlBuf);
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(highlighted), gl.DYNAMIC_DRAW);

            gl.useProgram(iconProg);

            gl.bindBuffer(gl.ARRAY_BUFFER, posBuf);
            const aPosIcon = gl.getAttribLocation(iconProg, "a_position");
            gl.enableVertexAttribArray(aPosIcon);
            gl.vertexAttribPointer(aPosIcon, 2, gl.FLOAT, false, 0, 0);

            gl.bindBuffer(gl.ARRAY_BUFFER, hlBuf);
            const aHlIcon = gl.getAttribLocation(iconProg, "a_highlighted");
            gl.enableVertexAttribArray(aHlIcon);
            gl.vertexAttribPointer(aHlIcon, 1, gl.FLOAT, false, 0, 0);

            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, iconTex);
            gl.uniform1i(gl.getUniformLocation(iconProg, "u_texture"), 0);
            gl.drawArrays(gl.POINTS, 0, count);
        }

        // --- Draw cluster bubbles via 2D canvas ---
        for (const dp of clusters) {
            if (!isFinite(dp.lng) || !isFinite(dp.lat)) continue;
            const pixel = projection.fromLatLngToContainerPixel(new google.maps.LatLng(dp.lat, dp.lng));
            if (!pixel) continue;

            const hovered = dp.clusterID !== undefined && hoveredIDs.has(`cluster_${dp.clusterID}`);
            const r2 = hovered ? 14 : 10;

            ctx2d.beginPath();
            ctx2d.arc(pixel.x, pixel.y, r2, 0, Math.PI * 2);
            ctx2d.fillStyle = CLUSTER_BG;
            ctx2d.fill();

            ctx2d.fillStyle = CLUSTER_TEXT;
            ctx2d.font = `bold ${hovered ? 13 : 10}px sans-serif`;
            ctx2d.textAlign = "center";
            ctx2d.textBaseline = "middle";
            ctx2d.fillText(String(dp.count ?? 0), pixel.x, pixel.y + 1);

            renderedPoints.push({ id: `cluster_${dp.clusterID}`, x: pixel.x, y: pixel.y, incidents: [], isCluster: true, clusterID: dp.clusterID });
        }

        ctx2d.setTransform(1, 0, 0, 1, 0, 0);
        onRenderedPoints(renderedPoints);
    }

    function startLoop() {
        if (rafId !== null) return;
        const loop = () => {
            draw();
            rafId = drawPoints.length > 0 ? requestAnimationFrame(loop) : null;
        };
        rafId = requestAnimationFrame(loop);
    }

    const mapListeners = [
        google.maps.event.addListener(map, "idle",           draw),
        google.maps.event.addListener(map, "bounds_changed", draw),
    ];

    return {
        setDrawPoints(next: DrawPoint[]) {
            drawPoints = next;
            if (next.length > 0) startLoop();
            else draw();
        },
        setHoveredIDs(ids: Set<string>) {
            hoveredIDs = ids;
            draw();
        },
        destroy() {
            if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
            mapListeners.forEach((l) => google.maps.event.removeListener(l));
            overlay.setMap(null);
            if (glCanvas.parentNode) glCanvas.parentNode.removeChild(glCanvas);
            if (clusterCanvas.parentNode) clusterCanvas.parentNode.removeChild(clusterCanvas);
        },
    };
}

type IncidentsOverlay = ReturnType<typeof createIncidentsOverlay>;

// --- Supercluster setup ---

type IncidentClusterProps = { incident: Incident };

function buildClusterIndex(incidents: Incident[]): Supercluster<IncidentClusterProps> {
    const index = new Supercluster<IncidentClusterProps>({ radius: CLUSTER_RADIUS, maxZoom: CLUSTER_MAX_ZOOM });
    const points: GeoJSON.Feature<GeoJSON.Point, IncidentClusterProps>[] = incidents.map((inc) => ({
        type: "Feature",
        properties: { incident: inc },
        geometry: { type: "Point", coordinates: [inc.position.coordinates[0], inc.position.coordinates[1]] },
    }));
    index.load(points);
    return index;
}

function pushClusters(
    map: google.maps.Map | null,
    overlay: IncidentsOverlay | null,
    index: Supercluster<IncidentClusterProps> | null,
    incidents: Incident[],
    cluster: boolean,
) {
    if (!overlay) return;
    if (!map || incidents.length === 0) {
        overlay.setDrawPoints([]);
        return;
    }

    if (!cluster || !index) {
        const drawPoints: DrawPoint[] = incidents.map((inc) => ({
            lng: inc.position.coordinates[0],
            lat: inc.position.coordinates[1],
            isCluster: false,
            incident: inc,
        }));
        overlay.setDrawPoints(drawPoints);
        return;
    }

    const zoom   = Math.floor(map.getZoom() ?? 0);
    const bounds = map.getBounds();
    if (!bounds) return;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const bbox: [number, number, number, number] = [sw.lng(), sw.lat(), ne.lng(), ne.lat()];

    const clusters = index.getClusters(bbox, zoom);

    const drawPoints: DrawPoint[] = clusters.map((c) => {
        const [lng, lat] = c.geometry.coordinates;
        const props = c.properties as any;
        if (props.cluster) {
            return { lng, lat, isCluster: true, clusterID: props.cluster_id as number, count: props.point_count as number };
        }
        return { lng, lat, isCluster: false, incident: (props as IncidentClusterProps).incident };
    });

    overlay.setDrawPoints(drawPoints);
}

// --- Severity radius map ---

function radiusForSeverity(_severity: string): number {
    return 2500;
}

// --- Hook return type ---

export type IncidentsLayerResult = {
    incidents: Incident[];
    wsState: ConnectionState;
    selectedIncident: IncidentDetail | null;
    modalOpen: boolean;
    setModalOpen: (open: boolean) => void;
    popupVisible: boolean;
    setPopupVisible: (v: boolean) => void;
    popupPosition: { x: number; y: number };
    popupIncidents: IncidentPreview[];
    popupPage: number;
    popupTotalCount: number;
    onPopupPageChange: (page: number) => void;
    onPopupSelectIncident: (id: string) => void;
    getHitsAtPoint: (clientX: number, clientY: number) => { incidents: Incident[]; hoveredID: string | null };
    setHoveredIDs: (ids: Set<string>) => void;
    openPopupForIncidents: (incidents: Incident[], position: { x: number; y: number }) => void;
};

const POPUP_PAGE_SIZE = 5;

// --- Hook ---

export function useIncidentsLayer(
    map: google.maps.Map | null,
    feedIDs: string[],
    showRadius: boolean,
    clusterIncidents: boolean,
): IncidentsLayerResult {
    const platformClient = usePlatformAPI();
    const [incidents, setIncidents] = useState<Incident[]>([]);
    const [wsState, setWsState] = useState<ConnectionState>(ConnectionState.DISCONNECTED);

    const [selectedIncident, setSelectedIncident] = useState<IncidentDetail | null>(null);
    const [modalOpen, setModalOpen] = useState(false);

    const [popupVisible, setPopupVisible] = useState(false);
    const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
    const [popupIncidents, setPopupIncidents] = useState<IncidentPreview[]>([]);
    const [popupPage, setPopupPage] = useState(1);
    const [popupTotalCount, setPopupTotalCount] = useState(0);

    const overlayRef = useRef<IncidentsOverlay | null>(null);
    const wsRef = useRef<WebSocketManager | null>(null);
    const incidentsRef = useRef<Incident[]>([]);
    const clusterIndexRef = useRef<Supercluster<IncidentClusterProps> | null>(null);
    const clusterIncidentsRef = useRef(clusterIncidents);
    const feedIDsRef = useRef(feedIDs);
    const batchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const circlesRef = useRef<google.maps.Circle[]>([]);
    const renderedPointsRef = useRef<RenderedPoint[]>([]);
    const popupAllIDsRef = useRef<string[]>([]);
    const modalFromPopupRef = useRef(false);

    // --- Hit testing ---

    function getIncidentsAtPoint(clientX: number, clientY: number): { incidents: Incident[]; hoveredID: string | null } {
        if (!map) return { incidents: [], hoveredID: null };
        const mapDiv = map.getDiv() as HTMLElement;
        const rect = mapDiv.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;

        let closestDist = Infinity;
        let closestPt: RenderedPoint | null = null;

        for (const pt of renderedPointsRef.current) {
            const dist = Math.hypot(pt.x - x, pt.y - y);
            if (dist <= CLICK_RADIUS && dist < closestDist) {
                closestDist = dist;
                closestPt = pt;
            }
        }

        if (!closestPt) return { incidents: [], hoveredID: null };

        if (closestPt.isCluster && closestPt.clusterID !== undefined && clusterIndexRef.current) {
            const leaves = clusterIndexRef.current.getLeaves(closestPt.clusterID, Infinity).map((l) => l.properties.incident);
            return { incidents: leaves, hoveredID: `cluster_${closestPt.clusterID}` };
        }

        // In non-cluster mode collect all individual points within click radius
        if (!clusterIncidentsRef.current) {
            const all = renderedPointsRef.current
                .filter((pt) => !pt.isCluster && Math.hypot(pt.x - x, pt.y - y) <= CLICK_RADIUS)
                .flatMap((pt) => pt.incidents);
            return { incidents: all, hoveredID: closestPt.incidents[0]?.id ?? null };
        }

        return { incidents: closestPt.incidents, hoveredID: closestPt.incidents[0]?.id ?? null };
    }

    // --- Update filters without reconnecting when feedIDs changes ---

    useEffect(() => {
        feedIDsRef.current = feedIDs;
        if (feedIDs.length === 0) {
            incidentsRef.current = [];
            clusterIndexRef.current = null;
            setIncidents([]);
            overlayRef.current?.setDrawPoints([]);
            return;
        }
        if (!wsRef.current?.isConnected()) return;
        const now = new Date();
        const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
        const end   = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();
        wsRef.current.send("setFilters", {
            filters: {
                feedIDs,
                dateRange: { start, end },
                severities: ["major", "extreme"],
                tags: { include: ["high_severity"], includeMatchAll: false },
                severityOrTags: true,
            },
        });
    }, [feedIDs]);

    // --- Keep clusterIncidents ref in sync and re-push when toggle changes ---

    useEffect(() => {
        clusterIncidentsRef.current = clusterIncidents;
        pushClusters(map, overlayRef.current, clusterIndexRef.current, incidentsRef.current, clusterIncidents);
    }, [clusterIncidents, map]);

    // --- Overlay lifecycle (always active when map exists) ---

    useEffect(() => {
        if (!map) return;

        const overlay = createIncidentsOverlay(map, (pts) => {
            renderedPointsRef.current = pts;
        });
        overlayRef.current = overlay;

        const listeners = [
            google.maps.event.addListener(map, "idle",           () => pushClusters(map, overlayRef.current, clusterIndexRef.current, incidentsRef.current, clusterIncidentsRef.current)),
            google.maps.event.addListener(map, "bounds_changed", () => pushClusters(map, overlayRef.current, clusterIndexRef.current, incidentsRef.current, clusterIncidentsRef.current)),
        ];

        return () => {
            overlay.destroy();
            overlayRef.current = null;
            listeners.forEach((l) => google.maps.event.removeListener(l));
        };
    }, [map]);

    // --- WebSocket lifecycle (only when feeds are selected) ---

    const hasFeeds = feedIDs.length > 0;

    useEffect(() => {
        if (!map || !hasFeeds) return;

        const now = new Date();
        const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
        const end   = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();

        const flushBatch = () => {
            const updated = [...incidentsRef.current];
            clusterIndexRef.current = updated.length > 0 ? buildClusterIndex(updated) : null;
            setIncidents(updated);
            pushClusters(map, overlayRef.current, clusterIndexRef.current, updated, clusterIncidentsRef.current);
        };

        const scheduleBatch = () => {
            if (batchTimerRef.current) clearTimeout(batchTimerRef.current);
            batchTimerRef.current = setTimeout(flushBatch, 200);
        };

        const handlers: MessageHandlers = {
            snapshot: (data: { incidents: Incident[] }) => {
                incidentsRef.current = data.incidents ?? [];
                if (batchTimerRef.current) clearTimeout(batchTimerRef.current);
                flushBatch();
            },
            created: (data: { incident: Incident }) => {
                if (data.incident) {
                    incidentsRef.current = [...incidentsRef.current, data.incident];
                    scheduleBatch();
                }
            },
            updated: (data: { incident: Incident }) => {
                if (data.incident) {
                    const idx = incidentsRef.current.findIndex((i) => i.id === data.incident.id);
                    if (idx >= 0) {
                        const next = [...incidentsRef.current];
                        next[idx] = data.incident;
                        incidentsRef.current = next;
                    } else {
                        incidentsRef.current = [...incidentsRef.current, data.incident];
                    }
                    scheduleBatch();
                }
            },
            incidentDetails: (data: { incident: IncidentDetail }) => {
                if (data.incident) {
                    setSelectedIncident(data.incident);
                    setModalOpen(true);
                }
            },
            incidentsPreview: (data: { incidents: IncidentPreview[] }) => {
                if (data.incidents) {
                    const ordered = [...data.incidents].sort((a, b) => {
                        return popupAllIDsRef.current.indexOf(a.id) - popupAllIDsRef.current.indexOf(b.id);
                    });
                    setPopupIncidents(ordered);
                    setPopupVisible(true);
                }
            },
        };

        const ws = platformClient.ws("/ws/incidents", handlers, (state) => {
            setWsState(state);
            if (state === ConnectionState.CONNECTED) {
                ws.send("setFilters", {
                    filters: {
                        feedIDs: feedIDsRef.current,
                        dateRange: { start, end },
                        severities: ["major", "extreme"],
                        tags: { include: ["high_severity"], includeMatchAll: false },
                        severityOrTags: true,
                    },
                });
            }
        });
        wsRef.current = ws;
        ws.connect();

        return () => {
            if (batchTimerRef.current) { clearTimeout(batchTimerRef.current); batchTimerRef.current = null; }
            ws.disconnect();
            wsRef.current = null;
            incidentsRef.current = [];
            clusterIndexRef.current = null;
            setIncidents([]);
            setWsState(ConnectionState.DISCONNECTED);
            overlayRef.current?.setDrawPoints([]);
        };
    }, [map, hasFeeds, platformClient]); // feedIDs changes handled separately via feedIDsRef

    // --- Popup page change ---

    const onPopupPageChange = (page: number) => {
        const allIDs = popupAllIDsRef.current;
        const pageIDs = allIDs.slice((page - 1) * POPUP_PAGE_SIZE, page * POPUP_PAGE_SIZE);
        setPopupPage(page);
        if (wsRef.current?.isConnected()) {
            wsRef.current.send("getIncidentsPreview", { incidentIDs: pageIDs });
        }
    };

    const onPopupSelectIncident = (id: string) => {
        modalFromPopupRef.current = true;
        setPopupVisible(false);
        if (wsRef.current?.isConnected()) {
            wsRef.current.send("getIncident", { incidentID: id });
        }
    };

    // --- Radius circles ---

    useEffect(() => {
        circlesRef.current.forEach((c) => c.setMap(null));
        circlesRef.current = [];

        if (!map || !showRadius || incidents.length === 0) return;

        const circles = incidents.map((incident) => {
            const [lng, lat] = incident.position.coordinates;
            return new google.maps.Circle({
                map,
                center: { lat, lng },
                radius: radiusForSeverity(incident.severity),
                strokeColor: "#ff0000",
                strokeOpacity: 0.8,
                strokeWeight: 1,
                fillColor: "#ff0000",
                fillOpacity: 0.2,
            });
        });

        circlesRef.current = circles;

        return () => {
            circles.forEach((c) => c.setMap(null));
            circlesRef.current = [];
        };
    }, [incidents, showRadius, map]);

    // --- Hover ID includes cluster IDs ---

    const getHitsAtPoint = (clientX: number, clientY: number): { incidents: Incident[]; hoveredID: string | null } => {
        return getIncidentsAtPoint(clientX, clientY);
    };

    const setHoveredIDs = (ids: Set<string>) => {
        overlayRef.current?.setHoveredIDs(ids);
    };

    const openPopupForIncidents = (nearbyIncidents: Incident[], position: { x: number; y: number }) => {
        if (nearbyIncidents.length === 0) return;
        if (nearbyIncidents.length === 1) {
            setPopupVisible(false);
            if (wsRef.current?.isConnected()) {
                wsRef.current.send("getIncident", { incidentID: nearbyIncidents[0].id });
            }
        } else {
            const allIDs = nearbyIncidents.map((i) => i.id);
            popupAllIDsRef.current = allIDs;
            const firstPageIDs = allIDs.slice(0, POPUP_PAGE_SIZE);
            setPopupPosition(position);
            setPopupPage(1);
            setPopupTotalCount(allIDs.length);
            if (wsRef.current?.isConnected()) {
                wsRef.current.send("getIncidentsPreview", { incidentIDs: firstPageIDs });
            }
        }
    };

    return {
        incidents,
        wsState,
        selectedIncident,
        modalOpen,
        setModalOpen,
        popupVisible,
        setPopupVisible,
        popupPosition,
        popupIncidents,
        popupPage,
        popupTotalCount,
        onPopupPageChange,
        onPopupSelectIncident,
        getHitsAtPoint,
        setHoveredIDs,
        openPopupForIncidents,
    };
}
